import { Point, IsoPoint } from '@/app/types/cad';

// Isometric grid constants
export const ISO_ANGLE = Math.PI / 6; // 30 degrees
export const GRID_SIZE = 20; // Base grid size in pixels

/**
 * Convert isometric grid coordinates to screen coordinates
 */
export function isoToScreen(iso: IsoPoint, gridSize: number = GRID_SIZE): Point {
  const x = (iso.u - iso.v) * Math.cos(ISO_ANGLE) * gridSize;
  const y = (iso.u + iso.v) * Math.sin(ISO_ANGLE) * gridSize;
  return { x, y };
}

/**
 * Convert screen coordinates to isometric grid coordinates
 */
export function screenToIso(screen: Point, gridSize: number = GRID_SIZE): IsoPoint {
  const cosAngle = Math.cos(ISO_ANGLE);
  const sinAngle = Math.sin(ISO_ANGLE);
  
  const u = (screen.x / (cosAngle * gridSize) + screen.y / (sinAngle * gridSize)) / 2;
  const v = (screen.y / (sinAngle * gridSize) - screen.x / (cosAngle * gridSize)) / 2;
  
  return { u, v };
}

/**
 * Snap point to nearest grid intersection
 */
export function snapToGrid(iso: IsoPoint, snapSize: number = 1): IsoPoint {
  return {
    u: Math.round(iso.u / snapSize) * snapSize,
    v: Math.round(iso.v / snapSize) * snapSize,
  };
}

/**
 * Calculate distance between two points
 */
export function distance(p1: Point, p2: Point): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Check if two isometric points are equal
 */
export function isoPointsEqual(p1: IsoPoint, p2: IsoPoint): boolean {
  return p1.u === p2.u && p1.v === p2.v;
}

/**
 * Transform screen point with view state (zoom and pan)
 */
export function screenToWorld(
  screen: Point,
  viewState: { zoom: number; panX: number; panY: number }
): Point {
  return {
    x: (screen.x - viewState.panX) / viewState.zoom,
    y: (screen.y - viewState.panY) / viewState.zoom,
  };
}

/**
 * Transform world point to screen with view state
 */
export function worldToScreen(
  world: Point,
  viewState: { zoom: number; panX: number; panY: number }
): Point {
  return {
    x: world.x * viewState.zoom + viewState.panX,
    y: world.y * viewState.zoom + viewState.panY,
  };
}
